import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  InputGroupAddon,
  InputGroupText,
  FormGroup,
  InputGroup,
} from 'reactstrap';
import React from 'react';
import { useMemo, useRef } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { get, map } from 'lodash-es';
import { useMutation, useQueryClient } from 'react-query';
import { createCharger } from '../../utils/queries/hostQueries';
import { toast } from 'react-toastify';
import { createUseStyles } from 'react-jss';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from 'react-leaflet';
import 'rc-time-picker/assets/index.css';
import TimePicker from 'rc-time-picker';
import moment from 'moment';
import vendors from '../../utils/vendors';
import { vehicleTypes } from '../../utils/misc';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import L from 'leaflet';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
});

L.Marker.prototype.options.icon = DefaultIcon;

const useStyles = createUseStyles({
  modal: {
    '& .modal-content': {
      width: '700px',
    },
    '& .modal-title': {
      fontSize: '17px',
    },
  },
  addMore: {
    marginTop: '15px',
    backgroundColor: '#2675b8',
    color: 'white',
    border: 'none',
    '&:hover': {
      backgroundColor: '#2675b8',
      color: 'white',
    },
  },
  addCharger: {
    backgroundColor: '#2675b8',
    color: 'white',
    border: 'none',
    '&:hover': {
      backgroundColor: '#2675b8',
      color: 'white',
    },
    marginTop: '15px',
    width: '100%',
  },
  timeRangePickerFrom: {
    width: '36%',
    marginLeft: '11px',
    borderRadius: '6px',
    backgroundColor: 'white',
    '& .rc-time-picker-input': {
      height: '38px',
      fontSize: '15px',
    },
  },
});

const center = {
  lat: 22.505,
  lng: 80.09,
};

function DraggableMarker({ position, setPosition }) {
  const map = useMapEvents({
    click() {
      map.locate();
    },
    locationfound(e) {
      setPosition('coords', {
        lat: e.latlng.lat,
        lng: e.latlng.lng,
      });
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        setPosition('coords', {
          lat: marker.getLatLng().lat,
          lng: marker.getLatLng().lng,
        });
      },
    }),
    [setPosition],
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    >
      <Popup minWidth={90}>
        <span>Drag and Drop the marker to set Coordinates</span>
      </Popup>
    </Marker>
  );
}

export default function CreateChargerModal({ toggleModalOpen, modalOpen }) {
  const classes = useStyles();
  const queryQlient = useQueryClient();
  const { mutate: addCharger } = useMutation(createCharger, {
    onSuccess: (data) => {
      queryQlient.fetchQuery('host/chargers');
      toggleModalOpen(false);
      toast.dark('Charger successfully added', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
    onError: (error) => {
      toast.dark(error.message, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  function convertToJoulepoinTimingFormatString(from, to) {
    return `${from}-${to}`;
  }
  return (
    <Modal
      isOpen={modalOpen}
      toggle={toggleModalOpen}
      className={classes.modal}
    >
      <ModalHeader toggle={toggleModalOpen}>Add New Charger</ModalHeader>
      <ModalBody style={{ marginTop: '-30px' }}>
        <Formik
          initialValues={{
            id: '',
            vendor: '',
            model: '',
            cpName: '',
            address: '',
            coords: {
              lat: center.lat,
              lng: center.lng,
            },
            connectors: [{ TYPE: 'CCS1' }],
            zipcode: 0,
            chargerType: 'AC',
            timingFrom: '',
            timingTo: '',
            energy: 7,
            tariff: 18,
            vehicleType: '4',
          }}
          onSubmit={(values, { resetForm }) => {
            if (get(values, 'tariff') < 18) {
              return toast.dark('Tariff cannot be less than ₹18', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              });
            }
            if (!get(values, 'cpName')) {
              return toast.dark('Please provide a Chargepoint Name', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              });
            }
            const formattedValues = {
              ID: get(values, 'id'),
              NAME: get(values, 'cpName'),
              IS_DC: get(values, 'chargerType') === 'AC' ? false : true,
              MODEL: get(values, 'model'),
              VENDOR: get(values, 'vendor'),
              ADDRESS: get(values, 'address'),
              IS_ENABLED: 1,
              LATITUDE: get(values, 'coords.lat'),
              LONGITUDE: get(values, 'coords.lng'),
              ZIPCODE: get(values, 'zipcode'),
              CONNECTORS: get(values, 'connectors'),
              TIMING: convertToJoulepoinTimingFormatString(
                values.timingFrom,
                values.timingTo,
              ),
              ENERGY: get(values, 'energy'),
              BASE_KWH_COST: get(values, 'tariff'),
              VEHICLE_TYPE: get(values, 'vehicleType'),
              RESET_FORM: resetForm,
            };

            addCharger(formattedValues);
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              <div className="form-group">
                <div className="row">
                  <div className="col-12 mb-3">
                    <Label>Charger Id</Label>
                    <Field
                      name="id"
                      type="string"
                      className="form-control"
                      id="chargepointId"
                      aria-describedby="chargepoint"
                      placeholder="Leave Empty to auto generate"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Charger Name</Label>
                    <Field
                      name="cpName"
                      type="string"
                      className="form-control"
                      id="chargepointName"
                      aria-describedby="chargepoint"
                      placeholder="Charger Name"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Vendor</Label>
                    {/* <Field
                      name="vendor"
                      type="string"
                      className="form-control"
                      id="vendor"
                      aria-describedby="chargepoint"
                      placeholder="Vendor"
                      disabled={isSubmitting}
                    /> */}
                    <Field
                      as="select"
                      name="vendor"
                      disabled={isSubmitting}
                      id="vendor"
                      style={{
                        width: '100%',
                        height: '43px',
                        borderRadius: '6px',
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '1px solid #d4d9df',
                        color: '#8b9bac',
                      }}
                    >
                      {map(vendors, (vendor, v) => (
                        <option
                          value={vendor}
                          key={v}
                          style={{ color: '#8b9bac' }}
                        >
                          {vendor}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Model</Label>
                    <Field
                      name="model"
                      type="string"
                      className="form-control"
                      id="model"
                      aria-describedby="chargepoint"
                      placeholder="Model"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div
                    className="col-5 mb-3"
                    style={{ display: 'flex', alignItems: 'flex-end' }}
                  >
                    <div>
                      <Label>Charger Type</Label>
                      <label style={{ marginLeft: '20px' }}>
                        <Field type="radio" name="chargerType" value="AC" />{' '}
                        {'  '}AC
                      </label>
                      <label style={{ marginLeft: '20px' }}>
                        <Field type="radio" name="chargerType" value="DC" /> DC
                      </label>
                    </div>
                  </div>
                  <div className="col-12 mb-3 mt-2">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Label for="exampleTime">Open Timing</Label>

                      <TimePicker
                        name="timingFrom"
                        showSecond={false}
                        defaultValue={moment()}
                        className={classes.timeRangePickerFrom}
                        onChange={(value) => {
                          if (value) {
                            setFieldValue('timingFrom', value.format('HH:mm'));
                          }
                        }}
                        format="HH:mm"
                        inputReadOnly
                      />

                      <Label for="exampleTime" style={{ marginLeft: '8px' }}>
                        to
                      </Label>

                      <TimePicker
                        name="timingTo"
                        showSecond={false}
                        defaultValue={moment()}
                        className={classes.timeRangePickerFrom}
                        onChange={(value) => {
                          if (value) {
                            setFieldValue('timingTo', value.format('HH:mm'));
                          }
                        }}
                        format="HH:mm"
                        inputReadOnly
                      />
                    </div>
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Vehicle Type</Label>
                    {/* <Field
                      name="vendor"
                      type="string"
                      className="form-control"
                      id="vendor"
                      aria-describedby="chargepoint"
                      placeholder="Vendor"
                      disabled={isSubmitting}
                    /> */}
                    <Field
                      as="select"
                      name="vehicleType"
                      disabled={isSubmitting}
                      id="vendor"
                      style={{
                        width: '100%',
                        height: '43px',
                        borderRadius: '6px',
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '1px solid #d4d9df',
                        color: '#8b9bac',
                      }}
                    >
                      {map(vehicleTypes, (type, t) => (
                        <option
                          value={type.id}
                          key={t}
                          style={{ color: '#8b9bac' }}
                        >
                          {type.name}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="col-12 mt-1">
                    <div>
                      <FormGroup>
                        <Label>Tariff</Label>
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>₹</InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="number"
                            value={values.tariff}
                            onChange={(e) => {
                              if (e.target.value <= 30) {
                                setFieldValue('tariff', e.target.value);
                              }
                            }}
                            name="tariff"
                          />
                        </InputGroup>
                      </FormGroup>
                    </div>
                  </div>
                  <div className="col-12 mt-1">
                    <div>
                      <FormGroup>
                        <Label>Charger Capacity</Label>
                        <InputGroup>
                          <Input
                            value={values.energy}
                            onChange={(e) =>
                              setFieldValue('energy', e.target.value)
                            }
                            name="energy"
                            type="number"
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText>KW</InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </div>
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Address</Label>
                    <Field
                      name="address"
                      type="string"
                      className="form-control"
                      id="address"
                      aria-describedby="chargepoint"
                      placeholder="Address"
                      component="textarea"
                      rows="1"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <Label>Zipcode</Label>
                    <Field
                      name="zipcode"
                      type="number"
                      className="form-control"
                      id="zipcode"
                      aria-describedby="chargepoint"
                      placeholder="Zipcode"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <MapContainer
                      center={center}
                      zoom={4}
                      scrollWheelZoom={false}
                      style={{
                        height: '400px',
                      }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <DraggableMarker
                        position={values.coords}
                        setPosition={setFieldValue}
                      />
                    </MapContainer>
                  </div>

                  <div className="col-6 mb-3">
                    <Label>Longitude</Label>
                    <Field
                      name="coords.lng"
                      type="number"
                      className="form-control"
                      id="coordinateX"
                      aria-describedby="chargepoint"
                      placeholder="logitude"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <Label>Latitude</Label>
                    <Field
                      name="coords.lat"
                      type="number"
                      className="form-control"
                      id="coordinateY"
                      aria-describedby="chargepoint"
                      placeholder="latitude"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-12 ">
                    <Label>Connectors</Label>
                  </div>
                  <div className="col-12 mb-3 mt-2">
                    <FieldArray
                      name="connectors"
                      render={(arrayHelpers) => (
                        <div>
                          {values.connectors.map((connector, index) => (
                            <div
                              className="row"
                              key={index}
                              style={{ marginBottom: '10px' }}
                            >
                              <div
                                className="col-3"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Button
                                  color="danger"
                                  style={{
                                    marginRight: '10px',
                                    backgroundColor: '#f95656',
                                    border: 'none',
                                  }}
                                  size="sm"
                                  disabled={values.connectors.length < 2}
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <i class="fas fa-trash"></i>
                                </Button>
                                Connector {index + 1}
                              </div>
                              <div
                                className="col-9"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Field
                                  as="select"
                                  name={`connectors.${index}.TYPE`}
                                  disabled={isSubmitting}
                                  id="type"
                                  style={{
                                    width: '100%',
                                    borderRadius: '3px',
                                    padding: '10px',
                                    backgroundColor: 'white',
                                    border: '1px solid #d4d9df',
                                  }}
                                >
                                  <option value="CCS1">CCS-1</option>
                                  <option value="CCS2">CCS-2</option>
                                  <option value="CHADEMO">CHAdeMO</option>
                                  <option value="TYPE2">Type-2</option>
                                </Field>
                              </div>
                            </div>
                          ))}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Button
                              color="primary"
                              className={classes.addMore}
                              onClick={() =>
                                arrayHelpers.push({
                                  TYPE: '',
                                })
                              }
                              disabled={isSubmitting}
                            >
                              Add More
                            </Button>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <div className="col-12">
                    <Button
                      color="primary"
                      className={classes.addCharger}
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Add Charger
                    </Button>
                  </div>
                  {/* {afterCreateAlert ? (
                    <div className="col-12 mt-3">
                      <Alert
                        variant="success"
                        onClose={() => setAfterCreateAlert(false)}
                        dismissible
                      >
                        Chargepoint <b>{createdChargerId}</b> was created with
                        password <b>{createdChargerPassword}</b>
                      </Alert>

                      <Link to={`/dashboard/chargers/${createdChargerId}`}>
                        <Button variant="link" style={{ padding: '0px' }}>
                          <ArrowRightIcon
                            style={{
                              width: '16px',
                              marginRight: '5px',
                            }}
                          />
                          View Chargepoint
                        </Button>
                      </Link>
                    </div>
                  ) : null} */}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
}
